/**
 * offlineSupport.js
 * Utilities for enhancing offline support in the application
 */

import { secureLocalStorage } from './secureStorage';

/**
 * Configuration for offline support
 */
const config = {
  offlineStoragePrefix: 'offline_',
  syncQueueKey: 'offline_sync_queue',
  maxQueueSize: 100,
  syncInterval: 60000, // 1 minute in milliseconds
  offlinePageUrl: '/offline.html',
  criticalApiEndpoints: [],
  offlineFallbackData: {}
};

/**
 * Checks if the user is currently online
 * @returns {boolean} - Whether the user is online
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Registers event listeners for online/offline events
 * @param {Function} onlineCallback - Function to call when going online
 * @param {Function} offlineCallback - Function to call when going offline
 * @returns {Function} - Function to remove the event listeners
 */
export const registerConnectivityListeners = (onlineCallback, offlineCallback) => {
  const handleOnline = () => {
    console.log('Application is online');
    if (onlineCallback && typeof onlineCallback === 'function') {
      onlineCallback();
    }
  };
  
  const handleOffline = () => {
    console.log('Application is offline');
    if (offlineCallback && typeof offlineCallback === 'function') {
      offlineCallback();
    }
  };
  
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  // Return function to remove listeners
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};

/**
 * Saves data for offline use
 * @param {string} key - The key to store the data under
 * @param {any} data - The data to store
 */
export const saveForOffline = (key, data) => {
  const offlineKey = `${config.offlineStoragePrefix}${key}`;
  secureLocalStorage.setItem(offlineKey, {
    data,
    timestamp: Date.now()
  });
};

/**
 * Retrieves data saved for offline use
 * @param {string} key - The key to retrieve
 * @returns {any} - The stored data or null if not found
 */
export const getOfflineData = (key) => {
  const offlineKey = `${config.offlineStoragePrefix}${key}`;
  const storedItem = secureLocalStorage.getItem(offlineKey);
  
  return storedItem ? storedItem.data : null;
};

/**
 * Removes offline data
 * @param {string} key - The key to remove
 */
export const removeOfflineData = (key) => {
  const offlineKey = `${config.offlineStoragePrefix}${key}`;
  secureLocalStorage.removeItem(offlineKey);
};

/**
 * Adds an API request to the sync queue for later processing when online
 * @param {Object} request - The request to queue
 */
export const addToSyncQueue = (request) => {
  const queue = secureLocalStorage.getItem(config.syncQueueKey) || [];
  
  // Don't exceed max queue size
  if (queue.length >= config.maxQueueSize) {
    queue.shift(); // Remove oldest item
  }
  
  queue.push({
    ...request,
    timestamp: Date.now(),
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  });
  
  secureLocalStorage.setItem(config.syncQueueKey, queue);
};

/**
 * Processes the sync queue when the application comes online
 * @param {Function} processFunction - Function to process each queued request
 * @returns {Promise<Array>} - Results of processing the queue
 */
export const processSyncQueue = async (processFunction) => {
  if (!isOnline()) {
    console.log('Cannot process sync queue while offline');
    return [];
  }
  
  const queue = secureLocalStorage.getItem(config.syncQueueKey) || [];
  
  if (queue.length === 0) {
    return [];
  }
  
  console.log(`Processing sync queue with ${queue.length} items`);
  
  const results = [];
  const newQueue = [];
  
  for (const request of queue) {
    try {
      if (processFunction && typeof processFunction === 'function') {
        const result = await processFunction(request);
        results.push({ request, result, success: true });
      } else {
        // Default processing if no function provided
        const response = await fetch(request.url, {
          method: request.method || 'POST',
          headers: request.headers || { 'Content-Type': 'application/json' },
          body: request.body ? JSON.stringify(request.body) : undefined
        });
        
        const result = await response.json();
        results.push({ request, result, success: response.ok });
      }
    } catch (error) {
      console.error('Error processing sync request:', error);
      results.push({ request, error: error.message, success: false });
      
      // Keep failed requests in the queue for retry
      newQueue.push(request);
    }
  }
  
  // Update queue with only failed requests
  secureLocalStorage.setItem(config.syncQueueKey, newQueue);
  
  return results;
};

/**
 * Sets up automatic sync queue processing when the app comes online
 * @param {Function} processFunction - Function to process each queued request
 * @returns {Function} - Function to stop automatic processing
 */
export const setupAutomaticSync = (processFunction) => {
  let intervalId = null;
  
  const handleOnline = async () => {
    await processSyncQueue(processFunction);
    
    // Set up interval to keep checking the queue
    if (intervalId === null) {
      intervalId = setInterval(async () => {
        if (isOnline()) {
          await processSyncQueue(processFunction);
        }
      }, config.syncInterval);
    }
  };
  
  const handleOffline = () => {
    // Clear interval when offline
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };
  
  // Register listeners
  const removeListeners = registerConnectivityListeners(handleOnline, handleOffline);
  
  // Initial processing if online
  if (isOnline()) {
    handleOnline();
  }
  
  // Return function to stop automatic sync
  return () => {
    removeListeners();
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };
};

/**
 * Prefetches and caches critical API responses for offline use
 * @param {Array<Object>} endpoints - Array of endpoint objects with url and options
 */
export const prefetchCriticalData = async (endpoints = config.criticalApiEndpoints) => {
  if (!endpoints || endpoints.length === 0) return;
  
  for (const endpoint of endpoints) {
    try {
      const response = await fetch(endpoint.url, endpoint.options);
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.warn(`Skipping prefetch for ${endpoint.url}: Response is not JSON`);
        continue;
      }
      
      if (!response.ok) {
        console.warn(`Skipping prefetch for ${endpoint.url}: HTTP ${response.status}`);
        continue;
      }
      
      const data = await response.json();
      saveForOffline(endpoint.key || endpoint.url, data);
      console.log(`Prefetched and cached data for ${endpoint.url}`);
    } catch (error) {
      console.warn(`Failed to prefetch ${endpoint.url}:`, error.message);
    }
  }
};

/**
 * Sets up fallback data for critical parts of the application when offline
 * @param {Object} fallbackData - Object with keys and fallback data
 */
export const setupOfflineFallbacks = (fallbackData = config.offlineFallbackData) => {
  if (!fallbackData || Object.keys(fallbackData).length === 0) return;
  
  Object.entries(fallbackData).forEach(([key, data]) => {
    // Only save if we don't already have offline data for this key
    if (!getOfflineData(key)) {
      saveForOffline(key, data);
      console.log(`Set up offline fallback data for ${key}`);
    }
  });
};

/**
 * Redirects to the offline page when the user is offline
 * @param {string} offlinePageUrl - URL of the offline page
 */
export const redirectToOfflinePage = (offlinePageUrl = config.offlinePageUrl) => {
  if (!isOnline() && window.location.pathname !== offlinePageUrl) {
    window.location.href = offlinePageUrl;
  }
};

/**
 * Checks if a fetch request should use cached data when offline
 * @param {string} url - The URL being fetched
 * @param {Object} options - Fetch options
 * @returns {boolean} - Whether to use cached data
 */
export const shouldUseCachedData = (url, options = {}) => {
  // Only use cached data for GET requests when offline
  return !isOnline() && (!options.method || options.method === 'GET');
};

/**
 * Creates a fetch wrapper that handles offline scenarios
 * @returns {Function} - The wrapped fetch function
 */
export const createOfflineFetch = () => {
  const originalFetch = window.fetch;
  
  window.fetch = async (url, options = {}) => {
    // If online, use normal fetch
    if (isOnline()) {
      try {
        const response = await originalFetch(url, options);
        
        // Cache GET responses for offline use
        if ((!options.method || options.method === 'GET') && response.ok) {
          try {
            const clonedResponse = response.clone();
            const data = await clonedResponse.json();
            saveForOffline(url, data);
          } catch (e) {
            // Ignore caching errors
          }
        }
        
        return response;
      } catch (error) {
        // If fetch fails, try to use cached data
        if (shouldUseCachedData(url, options)) {
          const cachedData = getOfflineData(url);
          if (cachedData) {
            return new Response(JSON.stringify(cachedData), {
              status: 200,
              headers: { 'Content-Type': 'application/json' }
            });
          }
        }
        
        throw error;
      }
    } else {
      // If offline, check for cached data
      if (shouldUseCachedData(url, options)) {
        const cachedData = getOfflineData(url);
        if (cachedData) {
          return new Response(JSON.stringify(cachedData), {
            status: 200,
            headers: { 'Content-Type': 'application/json' }
          });
        }
      }
      
      // For non-GET requests, add to sync queue
      if (options.method && options.method !== 'GET') {
        let body;
        try {
          body = JSON.parse(options.body);
        } catch (e) {
          body = options.body;
        }
        
        addToSyncQueue({
          url,
          method: options.method,
          headers: options.headers,
          body
        });
        
        // Return a mock successful response
        return new Response(JSON.stringify({ success: true, offlineQueued: true }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' }
        });
      }
      
      // If we get here, we're offline and have no cached data
      throw new Error('Network request failed and no cached data available');
    }
  };
  
  // Return function to restore original fetch
  return () => {
    window.fetch = originalFetch;
  };
};

/**
 * Initializes all offline support features
 * @param {Object} options - Configuration options
 * @returns {Object} - Object with cleanup functions
 */
export const initializeOfflineSupport = (options = {}) => {
  // Merge options with default config
  const mergedConfig = { ...config, ...options };
  
  // Set up connectivity listeners
  const removeListeners = registerConnectivityListeners(
    () => {
      // Dispatch custom event when online
      window.dispatchEvent(new CustomEvent('app:online'));
    },
    () => {
      // Dispatch custom event when offline
      window.dispatchEvent(new CustomEvent('app:offline'));
    }
  );
  
  // Set up automatic sync
  const stopAutoSync = setupAutomaticSync(options.processFunction);
  
  // Set up offline fetch wrapper
  const restoreFetch = createOfflineFetch();
  
  // Prefetch critical data
  prefetchCriticalData(mergedConfig.criticalApiEndpoints);
  
  // Set up offline fallbacks
  setupOfflineFallbacks(mergedConfig.offlineFallbackData);
  
  // Return cleanup functions
  return {
    removeListeners,
    stopAutoSync,
    restoreFetch
  };
};

/**
 * Detect user's preferred language
 * @returns {string} - The detected language code ('ar' or 'en')
 */
export const detectUserLanguage = () => {
  // Check if language is stored in localStorage
  const storedLanguage = localStorage.getItem('app_language');
  if (storedLanguage) {
    return storedLanguage;
  }
  
  // Check browser language
  const browserLang = navigator.language || navigator.userLanguage;
  if (browserLang) {
    // Check if it's Arabic
    if (browserLang.startsWith('ar')) {
      return 'ar';
    }
  }
  
  // Check HTML dir attribute
  const htmlDir = document.documentElement.dir;
  if (htmlDir === 'rtl') {
    return 'ar';
  }
  
  // Default to English
  return 'en';
};

/**
 * Default export with all offline support utilities
 */
export default {
  isOnline,
  registerConnectivityListeners,
  saveForOffline,
  getOfflineData,
  removeOfflineData,
  addToSyncQueue,
  processSyncQueue,
  setupAutomaticSync,
  prefetchCriticalData,
  setupOfflineFallbacks,
  redirectToOfflinePage,
  shouldUseCachedData,
  createOfflineFetch,
  initializeOfflineSupport,
  detectUserLanguage,
  config
};